import { AGENCY_PRINCIPAL_ROLE, AGENCY_PRINCIPAL_ROLE_PERMISSIONS } from '@ourbranch/staff-derived-roles';

import { buildSessionData } from 'core/components/auth/session-data';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';

// Agency users carry the ExternalSales group (#31758); getPermissions (auth.jsx) reads it by this name.
const AGENCY_AGENT_GROUPS = ['ExternalSales'];

// Only a Service rep who is Branch staff gets the control, judged on their real session.
export const canUseViewAs = (realSession) =>
  Boolean(
    realSession?.isLoggedIn &&
      realSession[CognitoPermissionGroups.isService] &&
      realSession[CognitoPermissionGroups.isInternalAgent]
  );

/**
 * The token attributes a typical agent at `subject.affinity` would sign in with. An agency is told
 * apart by its affinity attributes, not a group, and an unlicensed agent has unlicensed states and
 * no licensed ones, so both derive in getPermissions exactly as at login.
 */
export const buildViewAsAttributes = ({ realSession, subject, principal, unlicensed }) => {
  const states = JSON.stringify([subject.state]);
  return {
    'cognito:groups': [...AGENCY_AGENT_GROUPS, ...(principal ? AGENCY_PRINCIPAL_ROLE_PERMISSIONS : [])],
    'custom:affinity_groups': JSON.stringify([subject.affinity]),
    'custom:affinity_code': subject.affinity,
    'custom:allowed_states': unlicensed ? '[]' : states,
    'custom:unlicensed_states': unlicensed ? states : '[]',
    'custom:agency_role': principal ? AGENCY_PRINCIPAL_ROLE : '',
    given_name: 'Agency',
    family_name: 'agent',
    // Anything that sends the session's email or username (e.g. a rep field) still names the real user.
    email: realSession.user?.email
  };
};

/**
 * The session `useSession()` returns while the lens is on. The real session's functions, search
 * preferences and sign-in state carry over; only the permission flags and the displayed user change.
 */
export const buildViewAsSession = ({ realSession, subject, principal, unlicensed }) => ({
  ...realSession,
  ...buildSessionData({
    user: { username: realSession.user?.username },
    userAttributes: buildViewAsAttributes({ realSession, subject, principal, unlicensed }),
    userPreferences: realSession.userPreferences
  }),
  isImpersonating: realSession.isImpersonating,
  impersonatorEmail: realSession.impersonatorEmail
});
